// src/views/Admin/Companies/Companies.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  HiOutlineBuildingOffice,
  HiOutlinePlus,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineCheck,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineBuildingStorefront,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineIdentification,
  HiOutlineArchiveBoxXMark
} from 'react-icons/hi2';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  GetAllCompanies,
  CreateCompanyRegistration,
  UpdateCompany,
  DeleteCompany,
  PreviewEmptyCompanies,
  CleanEmptyCompanies,
  UpdateCompanyName,
  UpdateCompanyEmail,
  UpdateCompanyPhone,
  UpdateCompanyAddress,
  ToggleCompanyState
} from '../../../crud/configuration_api/companies';
import MiniLoadingSpinner from '../../../components/Spinners/MiniLoadingSpinner';
import { 
  DeleteCompanyModal, 
  CleanCompaniesModal, 
  CompanyFormModal,
  CreateLocalModal,
  InlineEditField,
  StatusToggle,
  CompanyFormData,
  LocalFormData
} from './components';
import { CreateLocal } from '../../../crud/management_api/locales';
import { getAllCities } from '../../../crud/configuration_api/places';
import './Companies.scss';

// City type
interface City {
  city_id: number;
  city_name: string;
}

// Types
interface Local {
  local_id: number;
  local_name: string;
}

interface Company {
  company_id: number;
  company_name: string;
  company_rut: string;
  company_email: string;
  company_phone: string;
  admin_name: string;
  address: string;
  state: boolean;
  company_code: string;
  plan_state: boolean;
  city_id: number;
  created_at: string;
  locales: Local[];
}

const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [citiesMap, setCitiesMap] = useState<Map<number, string>>(new Map());
  const [citiesList, setCitiesList] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState<'all' | 'active' | 'inactive'>('all');
  const [expandedCompany, setExpandedCompany] = useState<number | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCleanModal, setShowCleanModal] = useState(false);
  const [showCreateLocalModal, setShowCreateLocalModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Load companies and cities on mount
  useEffect(() => {
    loadCompanies();
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const response = await getAllCities();
      const map = new Map<number, string>();
      response.data.forEach((city: City) => {
        map.set(city.city_id, city.city_name);
      });
      setCitiesMap(map);
      setCitiesList(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const getCityName = (cityId: number): string => {
    return citiesMap.get(cityId) || `Ciudad ${cityId}`;
  };

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await GetAllCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Error al cargar compañías');
    } finally {
      setLoading(false);
    }
  };

  // Filter companies
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesSearch = 
        company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.company_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.company_rut?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.admin_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = 
        filterState === 'all' ||
        (filterState === 'active' && company.state) ||
        (filterState === 'inactive' && !company.state);

      return matchesSearch && matchesFilter;
    });
  }, [companies, searchTerm, filterState]);

  const activeCount = companies.filter(c => c.state).length;
  const inactiveCount = companies.length - activeCount;

  // Modal handlers
  const handleCreate = async (data: CompanyFormData) => {
    await CreateCompanyRegistration(data);
    toast.success('Compañía creada exitosamente');
    loadCompanies();
  };

  const handleUpdate = async (data: CompanyFormData) => {
    if (!selectedCompany) return;
    const updateData = { ...data };
    delete (updateData as any).company_password;
    await UpdateCompany(selectedCompany.company_id, updateData);
    toast.success('Compañía actualizada exitosamente');
    setSelectedCompany(null);
    loadCompanies();
  };

  const handleDelete = async () => {
    if (!selectedCompany) return;
    await DeleteCompany(selectedCompany.company_id);
    toast.success('Compañía eliminada exitosamente');
    setSelectedCompany(null);
    loadCompanies();
  };

  const handleFetchEmptyPreview = async () => {
    const response = await PreviewEmptyCompanies();
    return response.data.companies || [];
  };

  const handleCleanEmpty = async () => {
    const response = await CleanEmptyCompanies();
    toast.success(`${response.data.deleted_count} compañías eliminadas`);
    loadCompanies();
  };

  // Inline update handlers
  const updateCompanyInState = (companyId: number, updates: Partial<Company>) => {
    setCompanies(prev => prev.map(c => 
      c.company_id === companyId ? { ...c, ...updates } : c
    ));
  };

  const handleInlineUpdateName = async (companyId: number, newName: string) => {
    const response = await UpdateCompanyName(companyId, newName);
    updateCompanyInState(companyId, { company_name: response.data.company_name });
    toast.success('Nombre actualizado');
  };

  const handleInlineUpdateEmail = async (companyId: number, newEmail: string) => {
    const response = await UpdateCompanyEmail(companyId, newEmail);
    updateCompanyInState(companyId, { company_email: response.data.company_email });
    toast.success('Email actualizado');
  };

  const handleInlineUpdatePhone = async (companyId: number, newPhone: string) => {
    const response = await UpdateCompanyPhone(companyId, newPhone);
    updateCompanyInState(companyId, { company_phone: response.data.company_phone });
    toast.success('Teléfono actualizado');
  };

  const handleInlineUpdateAddress = async (companyId: number, newAddress: string) => {
    const response = await UpdateCompanyAddress(companyId, newAddress);
    updateCompanyInState(companyId, { address: response.data.address });
    toast.success('Dirección actualizada');
  };

  const handleToggleState = async (companyId: number) => {
    const response = await ToggleCompanyState(companyId);
    updateCompanyInState(companyId, { state: response.data.state });
    toast.success(response.data.state ? 'Compañía activada' : 'Compañía desactivada');
  };

  const openEditModal = (company: Company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const openDeleteModal = (company: Company) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const openCreateLocalModal = (company: Company) => {
    setSelectedCompany(company);
    setShowCreateLocalModal(true);
  };

  const handleCreateLocal = async (data: LocalFormData) => {
    await CreateLocal(data);
    toast.success('Local creado exitosamente');
    loadCompanies();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getEditFormData = (): CompanyFormData | undefined => {
    if (!selectedCompany) return undefined;
    return {
      company_name: selectedCompany.company_name,
      company_password: '',
      company_rut: selectedCompany.company_rut || '',
      company_email: selectedCompany.company_email,
      company_phone: selectedCompany.company_phone || '',
      admin_name: selectedCompany.admin_name || '',
      address: selectedCompany.address || '',
      state: selectedCompany.state,
      company_code: selectedCompany.company_code || '',
      plan_state: selectedCompany.plan_state,
      city_id: selectedCompany.city_id
    };
  };

  if (loading) {
    return (
      <div className="companies-loading">
        <MiniLoadingSpinner />
        <p>Cargando compañías...</p>
      </div>
    );
  }

  return (
    <div className="companies-page">
      {/* Header */}
      <div className="companies-header">
        <div className="header-info">
          <h1>Compañías</h1>
          <p>Gestión de compañías registradas en MedioPunto</p>
        </div>
        <div className="header-actions">
          <button 
            className="clean-btn"
            onClick={() => setShowCleanModal(true)}
            title="Limpiar compañías vacías"
          >
            <HiOutlineArchiveBoxXMark size={18} />
            <span>Limpiar Vacías</span>
          </button>
          <button 
            className="refresh-btn"
            onClick={loadCompanies}
            title="Recargar"
          >
            <FiRefreshCw size={18} />
          </button>
          <button 
            className="create-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <HiOutlinePlus size={18} />
            <span>Nueva Compañía</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="companies-stats">
        <div className="stat-card">
          <HiOutlineBuildingOffice size={24} />
          <div className="stat-info">
            <span className="stat-value">{companies.length}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className="stat-card active">
          <HiOutlineCheck size={24} />
          <div className="stat-info">
            <span className="stat-value">{activeCount}</span>
            <span className="stat-label">Activas</span>
          </div>
        </div>
        <div className="stat-card inactive">
          <HiOutlineXMark size={24} />
          <div className="stat-info">
            <span className="stat-value">{inactiveCount}</span>
            <span className="stat-label">Inactivas</span>
          </div>
        </div>
        <div className="stat-card locals">
          <HiOutlineBuildingStorefront size={24} />
          <div className="stat-info">
            <span className="stat-value">
              {companies.reduce((sum, c) => sum + (c.locales?.length || 0), 0)}
            </span>
            <span className="stat-label">Locales</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="companies-filters">
        <div className="search-box">
          <FiSearch size={18} />
          <input
            type="text"
            placeholder="Buscar por nombre, email, RUT o admin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-buttons">
          <button 
            className={filterState === 'all' ? 'active' : ''}
            onClick={() => setFilterState('all')}
          >
            Todas
          </button>
          <button 
            className={filterState === 'active' ? 'active' : ''}
            onClick={() => setFilterState('active')}
          >
            Activas
          </button>
          <button 
            className={filterState === 'inactive' ? 'active' : ''}
            onClick={() => setFilterState('inactive')}
          >
            Inactivas
          </button>
        </div>
      </div>

      {/* Companies List */}
      <div className="companies-list">
        {filteredCompanies.length === 0 ? (
          <div className="no-companies">
            <HiOutlineBuildingOffice size={48} />
            <p>No se encontraron compañías</p>
          </div>
        ) : (
          filteredCompanies.map(company => (
            <div 
              key={company.company_id} 
              className={`company-row ${expandedCompany === company.company_id ? 'expanded' : ''}`}
            >
              <div 
                className="company-row-main"
                onClick={() => setExpandedCompany(
                  expandedCompany === company.company_id ? null : company.company_id
                )}
              >
                <div className="company-icon">
                  <HiOutlineBuildingOffice size={24} />
                </div>
                
                <div className="company-primary">
                  <InlineEditField
                    value={company.company_name}
                    onSave={(val) => handleInlineUpdateName(company.company_id, val)}
                    className="large"
                    placeholder="Nombre de la compañía"
                  />
                  <div className="company-badges">
                    <StatusToggle
                      isActive={company.state}
                      onToggle={() => handleToggleState(company.company_id)}
                    />
                    <span className={`badge plan ${company.plan_state ? 'active' : 'inactive'}`}>
                      PLAN {company.plan_state ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                    {company.locales?.length > 0 && (
                      <span className="badge locals">
                        {company.locales.length} LOCAL{company.locales.length !== 1 ? 'ES' : ''}
                      </span>
                    )}
                  </div>
                  <InlineEditField
                    value={company.company_email}
                    onSave={(val) => handleInlineUpdateEmail(company.company_id, val)}
                    icon={<HiOutlineEnvelope size={14} />}
                    type="email"
                    placeholder="email@empresa.com"
                    className="compact"
                  />
                </div>

                <div className="company-secondary">
                  <InlineEditField
                    value={company.company_phone || ''}
                    onSave={(val) => handleInlineUpdatePhone(company.company_id, val)}
                    icon={<HiOutlinePhone size={16} />}
                    type="tel"
                    placeholder="+56 9 1234 5678"
                    emptyText="Sin teléfono"
                    className="compact"
                  />
                </div>

                <div className="company-tertiary">
                  {company.company_rut && (
                    <div className="info-cell">
                      <HiOutlineIdentification size={16} />
                      <span>{company.company_rut}</span>
                    </div>
                  )}
                </div>

                <div className="company-actions">
                  <button 
                    className="action-btn add-local-btn"
                    onClick={(e) => { e.stopPropagation(); openCreateLocalModal(company); }}
                    title="Crear local"
                  >
                    <HiOutlineBuildingStorefront size={18} />
                  </button>
                  <button 
                    className="action-btn edit-btn"
                    onClick={(e) => { e.stopPropagation(); openEditModal(company); }}
                    title="Editar compañía"
                  >
                    <HiOutlinePencilSquare size={18} />
                  </button>
                  <button 
                    className="action-btn delete-btn"
                    onClick={(e) => { e.stopPropagation(); openDeleteModal(company); }}
                    title="Eliminar compañía"
                  >
                    <HiOutlineTrash size={18} />
                  </button>
                  <div className="expand-indicator">
                    {expandedCompany === company.company_id ? (
                      <HiOutlineChevronDown size={20} />
                    ) : (
                      <HiOutlineChevronRight size={20} />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedCompany === company.company_id && (
                <div className="company-row-expanded">
                  <div className="expanded-content">
                    <div className="info-section">
                      <h4>Información General</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <span className="label">ID</span>
                          <span className="value">{company.company_id}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Código</span>
                          <span className="value">{company.company_code || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Admin</span>
                          <span className="value">{company.admin_name || 'N/A'}</span>
                        </div>
                        <div className="info-item editable">
                          <span className="label">Dirección</span>
                          <InlineEditField
                            value={company.address || ''}
                            onSave={(val) => handleInlineUpdateAddress(company.company_id, val)}
                            placeholder="Av. Principal 123"
                            emptyText="Sin dirección"
                            className="compact"
                          />
                        </div>
                        <div className="info-item">
                          <span className="label">Ciudad</span>
                          <span className="value">{getCityName(company.city_id)}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Creada</span>
                          <span className="value">{formatDate(company.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="locals-section">
                      <div className="locals-header">
                        <h4>
                          <HiOutlineBuildingStorefront size={18} />
                          Locales ({company.locales?.length || 0})
                        </h4>
                        <button 
                          className="add-local-inline-btn"
                          onClick={() => openCreateLocalModal(company)}
                        >
                          <HiOutlinePlus size={16} />
                          <span>Crear Local</span>
                        </button>
                      </div>
                      {company.locales?.length > 0 ? (
                        <div className="locals-list">
                          {company.locales.map(local => (
                            <div key={local.local_id} className="local-item">
                              <span className="local-name">{local.local_name}</span>
                              <span className="local-id">ID: {local.local_id}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-locals">Esta compañía no tiene locales</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      <CompanyFormModal
        isOpen={showCreateModal}
        mode="create"
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
      />

      <CompanyFormModal
        isOpen={showEditModal}
        mode="edit"
        initialData={getEditFormData()}
        onClose={() => { setShowEditModal(false); setSelectedCompany(null); }}
        onSubmit={handleUpdate}
      />

      <DeleteCompanyModal
        isOpen={showDeleteModal}
        companyName={selectedCompany?.company_name || ''}
        companyId={selectedCompany?.company_id || 0}
        locales={selectedCompany?.locales}
        onClose={() => { setShowDeleteModal(false); setSelectedCompany(null); }}
        onConfirm={handleDelete}
      />

      <CleanCompaniesModal
        isOpen={showCleanModal}
        onClose={() => setShowCleanModal(false)}
        onFetchPreview={handleFetchEmptyPreview}
        onConfirm={handleCleanEmpty}
      />

      <CreateLocalModal
        isOpen={showCreateLocalModal}
        companyId={selectedCompany?.company_id || 0}
        companyName={selectedCompany?.company_name || ''}
        companyAddress={selectedCompany?.address || ''}
        companyCityId={selectedCompany?.city_id || 1}
        cities={citiesList}
        onClose={() => { setShowCreateLocalModal(false); setSelectedCompany(null); }}
        onSubmit={handleCreateLocal}
      />
    </div>
  );
};

export default Companies;
